import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Phone, MapPin, Banknote, HandCoins, MessageSquare, Calendar,
  CheckCircle2, AlertTriangle, Trash2, UserCog, FileText, User as UserIcon
} from 'lucide-react';
import { OwnerLayout } from '../../../components/OwnerLayout';
import api from '../../../api';
import { encodeLan } from '../../../api';
import { captureGPS } from '../../../gps';

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

function fmtINR(n) {
  const num = Number(n || 0);
  return `\u20B9${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  } catch { return d; }
}

function fmtDay(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }); } catch { return d; }
}

const STATUSES = [
  ['new_overdue', 'New Overdue'],
  ['contact_attempted', 'Contact Attempted'],
  ['connected', 'Connected'],
  ['ptp_set', 'PTP Set'],
  ['partially_paid', 'Partially Paid'],
  ['regularized', 'Regularized'],
  ['settled_closed', 'Settled / Closed'],
  ['dispute_hold', 'Dispute Hold'],
  ['escalated_supervisor', 'Escalated'],
  ['legal_initiated', 'Legal Initiated'],
  ['written_off', 'Written Off'],
];
const STATUS_LABELS = Object.fromEntries(STATUSES);

const ACTION_TYPES = [
  ['call', 'Call'],
  ['visit', 'Field Visit'],
  ['sms', 'SMS'],
  ['whatsapp', 'WhatsApp'],
  ['letter', 'Letter / Notice'],
  ['meeting', 'Meeting'],
];

function getGps() {
  try {
    const raw = localStorage.getItem('au_gps');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed?.lat && parsed?.lng) return { gps_lat: parsed.lat, gps_lng: parsed.lng };
  } catch {}
  return {};
}

async function freshGps() {
  const c = await captureGPS();
  if (c && c.lat && c.lng) return { gps_lat: c.lat, gps_lng: c.lng };
  return getGps();
}

export default function RecoveryCaseDetail() {
  const { loanAccountNumber } = useParams();
  const navigate = useNavigate();
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const canAssign = currentUser.role === 'owner' || currentUser.role === 'manager';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, msg: '' });

  const [actionModal, setActionModal] = useState(false);
  const [actionForm, setActionForm] = useState({ actionType: 'call', outcome: '', notes: '', nextFollowupDate: '', statusChange: '' });

  const [promiseModal, setPromiseModal] = useState(false);
  const [promiseForm, setPromiseForm] = useState({ promiseAmount: 0, promiseDate: '', notes: '' });

  const [paymentModal, setPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, paymentMode: 'cash', transactionRef: '', notes: '' });

  // Submit-button guard: prevents double-click / React-fast-resubmit from
  // creating duplicate rows. Ref handles same-tick re-entry; state mirrors
  // it so the Save button can render as disabled.
  const submittingActionRef = useRef(false);
  const [submittingAction, setSubmittingAction] = useState(false);

  const [assignModal, setAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ assignedToUserId: '', supervisingManagerId: '' });
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  useEffect(() => {
    api.get('/users/access').then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/recovery/cases/${encodeLan(loanAccountNumber)}`);
      setData(res.data);
      try {
        const audit = await api.get('/recovery/audit-log', { params: { loanAccountNumber } });
        setAuditLog(audit.data || []);
      } catch { /* non-fatal */ }
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoading(false);
    }
  }, [loanAccountNumber]);

  useEffect(() => { load(); }, [load]);

  const submitAction = async () => {
    if (submittingActionRef.current) return;
    submittingActionRef.current = true;
    setSubmittingAction(true);
    try {
      const gps = await freshGps();
      const { statusChange, ...actionPayload } = actionForm;
      const wantsFlip = statusChange && statusChange !== c.status;
      await api.post(`/recovery/cases/${encodeLan(loanAccountNumber)}/actions`, {
        ...actionPayload,
        nextFollowupDate: actionForm.nextFollowupDate || null,
        ...(wantsFlip ? { newStatus: statusChange } : {}),
        ...gps,
      });
      setActionModal(false);
      setActionForm({ actionType: 'call', outcome: '', notes: '', nextFollowupDate: '', statusChange: '' });
      showToast(wantsFlip ? 'Action logged & status updated' : 'Action logged');
      load();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed');
    } finally {
      submittingActionRef.current = false;
      setSubmittingAction(false);
    }
  };

  const submitPromise = async () => {
    if (!promiseForm.promiseDate || Number(promiseForm.promiseAmount) <= 0) {
      showToast('Promise amount and date are required');
      return;
    }
    try {
      const gps = await freshGps();
      await api.post(`/recovery/cases/${encodeLan(loanAccountNumber)}/promises`, {
        promiseAmount: parseFloat(promiseForm.promiseAmount),
        promiseDate: promiseForm.promiseDate,
        notes: promiseForm.notes,
        ...gps,
      });
      setPromiseModal(false);
      setPromiseForm({ promiseAmount: 0, promiseDate: '', notes: '' });
      showToast('PTP recorded');
      load();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
  };

  const submitPayment = async () => {
    if (Number(paymentForm.amount) <= 0) { showToast('Amount must be greater than 0'); return; }
    try {
      const gps = await freshGps();
      await api.post(`/recovery/cases/${encodeLan(loanAccountNumber)}/payments`, {
        amount: parseFloat(paymentForm.amount),
        paymentMode: paymentForm.paymentMode,
        transactionRef: paymentForm.transactionRef,
        notes: paymentForm.notes,
        ...gps,
      });
      setPaymentModal(false);
      setPaymentForm({ amount: 0, paymentMode: 'cash', transactionRef: '', notes: '' });
      showToast('Payment recorded');
      load();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
  };

  const submitAssign = async () => {
    try {
      await api.patch(`/recovery/loans/${encodeLan(loanAccountNumber)}`, {
        assignedToUserId: assignForm.assignedToUserId || null,
        supervisingManagerId: assignForm.supervisingManagerId || null,
      });
      setAssignModal(false);
      showToast('Assignment updated');
      load();
    } catch (err) { showToast(err.response?.data?.detail || 'Failed'); }
  };

  const deleteLoan = async () => {
    if (!window.confirm(`Delete loan ${loanAccountNumber} and ALL recovery history?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/recovery/loans/${encodeLan(loanAccountNumber)}`);
      showToast('Loan deleted');
      setTimeout(() => navigate('/owner/recovery/cases'), 600);
    } catch (err) { showToast(err.response?.data?.detail || 'Delete failed'); }
  };

  if (loading || !data) {
    return (
      <OwnerLayout title="Recovery Case">
        <div className="panel" style={{ padding: '32px', textAlign: 'center', color: 'var(--g3)' }}>Loading…</div>
      </OwnerLayout>
    );
  }

  const { case: c, loan, customer, actions, promises, payments, overdueImport, reconciliations } = data;

  // Merge timeline events
  const timeline = [
    ...(actions || []).map((a) => ({ kind: 'action', ts: a.createdAt, data: a })),
    ...(promises || []).map((p) => ({ kind: 'promise', ts: p.createdAt, data: p })),
    ...(payments || []).map((p) => ({ kind: 'payment', ts: p.createdAt, data: p })),
  ].sort((a, b) => new Date(b.ts) - new Date(a.ts));

  return (
    <OwnerLayout title={`Case · ${loanAccountNumber}`}>
      <div className="case-header flex items-center justify-between mb-4">
        <div className="case-header-info flex items-center gap-2">
          <button className="btn-outline case-back-btn" onClick={() => navigate('/owner/recovery/cases')}>
            <ArrowLeft size={14} style={{ marginRight: '4px' }} /> Back
          </button>
          <div>
            <h2 className="font-heading font-bold text-navy text-lg">{customer?.fullName || c.clientId}</h2>
            <p className="text-sm text-gray">Loan {c.loanAccountNumber} · Client {c.clientId}</p>
          </div>
        </div>
        <div className="case-actions flex gap-2 flex-wrap">
          <button className="btn-navy case-act-primary" onClick={() => setPaymentModal(true)}>
            <Banknote size={14} style={{ marginRight: '4px' }} /> Record Payment
          </button>
          <button className="btn-outline" onClick={() => setPromiseModal(true)}>
            <HandCoins size={14} style={{ marginRight: '4px' }} /> Add PTP
          </button>
          <button className="btn-outline" onClick={() => { setActionForm((p) => ({ ...p, statusChange: '' })); setActionModal(true); }}>
            <MessageSquare size={14} style={{ marginRight: '4px' }} /> Log Action
          </button>
          {canAssign && (
            <button className="btn-outline" onClick={() => { setAssignForm({ assignedToUserId: loan?.assignedToUserId || '', supervisingManagerId: loan?.supervisingManagerId || '' }); setAssignModal(true); }}>
              <UserCog size={14} style={{ marginRight: '4px' }} /> Reassign
            </button>
          )}
        </div>
      </div>

      {/* Summary stats */}
      <div className="od-stats">
        <div className="od-stat">
          <div className="od-stat-lbl">Status</div>
          <div className="od-stat-val" style={{ fontSize: '20px' }}>{STATUS_LABELS[c.status] || c.status}</div>
          <div className="od-stat-sub">Updated {fmtDate(c.updatedAt)}</div>
        </div>
        <div className="od-stat accent">
          <div className="od-stat-lbl">Total Overdue</div>
          <div className="od-stat-val red">{fmtINR(loan?.totalOverdueAmount)}</div>
          <div className="od-stat-sub">
            {(loan?.bouncedEmis ?? 0) > 0
              ? <><strong>{loan.bouncedEmis} EMI bounced</strong> · DPD {loan?.dpdDays ?? 0}d ({loan?.dpdBucket || '0'})</>
              : <>No EMI bounced · current-cycle accrued interest</>}
          </div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl">EMI</div>
          <div className="od-stat-val" style={{ fontSize: '20px' }}>{fmtINR(loan?.emiAmount)}</div>
          <div className="od-stat-sub">{loan?.repaymentFrequency || 'monthly'}</div>
        </div>
        <div className="od-stat">
          <div className="od-stat-lbl">Next Follow-up</div>
          <div className="od-stat-val" style={{ fontSize: '20px' }}>{fmtDay(c.nextFollowupDate)}</div>
          <div className="od-stat-sub">Next due: {fmtDay(loan?.nextDueDate)}</div>
        </div>
      </div>

      {/* Overdue report freshness + reconciliation differences */}
      <OverdueMetaPanel overdueImport={overdueImport} reconciliations={reconciliations} loan={loan} />

      <div className="od-two-col" style={{ marginTop: '16px' }}>
        {/* Timeline */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-title"><Calendar size={14} /> Activity Timeline</span>
            <span style={{ fontSize: '11px', color: 'var(--g3)' }}>{timeline.length} event{timeline.length !== 1 ? 's' : ''}</span>
          </div>
          <div>
            {timeline.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--g3)', fontSize: '13px' }}>No activity yet</div>
            ) : timeline.map((ev, i) => <TimelineRow key={i} ev={ev} />)}
          </div>
          {auditLog.length > 0 && (
            <div style={{ borderTop: '1px solid var(--g2)', marginTop: '12px', paddingTop: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  onClick={() => setShowAudit((v) => !v)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--navy)', fontSize: '12px', fontWeight: 600, fontFamily: 'var(--fh)' }}
                >
                  {showAudit ? '▾' : '▸'} Audit Log ({auditLog.length})
                </button>
                {(() => { try { const u = JSON.parse(localStorage.getItem('au_user') || '{}'); return u.role === 'owner' || u.role === 'admin'; } catch { return false; } })() && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.get(`/recovery/audit-log/export?loanAccountNumber=${encodeLan(loanAccountNumber)}`, { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `audit_log_${loanAccountNumber}_${new Date().toISOString().slice(0,10)}.csv`);
                        document.body.appendChild(link); link.click(); link.remove();
                      } catch { alert('Export failed'); }
                    }}
                    style={{ background: 'none', border: '1px solid var(--g2)', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11, color: 'var(--navy)' }}
                  >
                    Export CSV
                  </button>
                )}
              </div>
              {showAudit && (
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {auditLog.map((row) => <AuditRow key={row.id} row={row} />)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar info */}
        <div>
          <div className="panel">
            <div className="panel-header">
              <span className="panel-title">Borrower</span>
            </div>
            <div style={{ padding: '4px 0', fontSize: '13px', color: 'var(--navy)' }}>
              <div style={{ fontWeight: 700, fontSize: '14px' }}>{customer?.fullName || '—'}</div>
              <div className="text-xs text-gray" style={{ marginTop: '4px' }}>Client ID: <strong>{c.clientId}</strong></div>
              {customer?.primaryPhone && (
                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Phone size={12} color="var(--g3)" />
                  <a href={`tel:${customer.primaryPhone}`} style={{ color: 'var(--navy)' }}>{customer.primaryPhone}</a>
                  {customer.alternatePhone && <span className="text-xs text-gray">· {customer.alternatePhone}</span>}
                </div>
              )}
              {customer?.address && (
                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px' }}>
                  <MapPin size={12} color="var(--g3)" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <span>{customer.address}</span>
                </div>
              )}
            </div>
          </div>

          <div className="panel" style={{ marginTop: '12px' }}>
            <div className="panel-header">
              <span className="panel-title">Loan Details</span>
            </div>
            <div style={{ padding: '4px 0', fontSize: '12px', color: 'var(--navy)', display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: '6px', columnGap: '12px' }}>
              <div><div className="text-xs text-gray">Product</div><div>{loan?.productType || '—'}</div></div>
              <div><div className="text-xs text-gray">Disbursed</div><div>{fmtDay(loan?.disbursementDate)}</div></div>
              <div><div className="text-xs text-gray">First EMI</div><div>{fmtDay(loan?.firstEmiDate)}</div></div>
              <div><div className="text-xs text-gray">Due Day</div><div>{loan?.installmentDueDay || '—'}</div></div>
              <div><div className="text-xs text-gray">Principal O/S</div><div>{fmtINR(loan?.outstandingPrincipal)}</div></div>
              <div><div className="text-xs text-gray">Interest O/S</div><div>{fmtINR(loan?.outstandingInterest)}</div></div>
              <div><div className="text-xs text-gray">Penalty</div><div>{fmtINR(loan?.penaltyAmount)}</div></div>
              <div><div className="text-xs text-gray">Last Paid</div><div>{fmtDay(loan?.lastPaidDate)}</div></div>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div style={{ marginTop: '24px', padding: '14px 16px', border: '1px solid #fecaca', background: '#fef2f2', borderRadius: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#991b1b', fontFamily: 'var(--fh)' }}>Danger Zone</div>
            <div style={{ fontSize: '11.5px', color: '#7f1d1d', marginTop: '2px' }}>
              Permanently delete this loan and ALL recovery history (cases, actions, promises, payments, reconciliations). This cannot be undone.
            </div>
          </div>
          <button className="btn-outline" style={{ color: 'var(--red)', borderColor: 'var(--red)', flexShrink: 0 }} onClick={deleteLoan}>
            <Trash2 size={14} style={{ marginRight: '4px' }} /> Delete Loan
          </button>
        </div>
      </div>

      {/* Modals */}
      {actionModal && (
        <Modal title="Log Action" icon={<MessageSquare size={16} />} onClose={() => setActionModal(false)}>
          <div className="form-group">
            <label className="form-label">Action Type</label>
            <select className="form-input" value={actionForm.actionType} onChange={(e) => setActionForm((p) => ({ ...p, actionType: e.target.value }))}>
              {ACTION_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Outcome (optional)</label>
            <input className="form-input" placeholder="e.g. customer agreed to pay" value={actionForm.outcome} onChange={(e) => setActionForm((p) => ({ ...p, outcome: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-input" rows={2} value={actionForm.notes} onChange={(e) => setActionForm((p) => ({ ...p, notes: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Update Status (optional)</label>
            <select className="form-input" value={actionForm.statusChange} onChange={(e) => setActionForm((p) => ({ ...p, statusChange: e.target.value }))}>
              <option value="">— No change (currently {STATUS_LABELS[c.status] || c.status}) —</option>
              {STATUSES.filter(([v]) => v !== c.status).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Next Follow-up Date (optional)</label>
            <input className="form-input" type="date" value={actionForm.nextFollowupDate} onChange={(e) => setActionForm((p) => ({ ...p, nextFollowupDate: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-outline" onClick={() => setActionModal(false)} disabled={submittingAction}>Cancel</button>
            <button className="btn-navy" onClick={submitAction} disabled={submittingAction} style={{ opacity: submittingAction ? 0.6 : 1, cursor: submittingAction ? 'not-allowed' : 'pointer' }}>
              {submittingAction ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {promiseModal && (
        <Modal title="Record Promise to Pay" icon={<HandCoins size={16} />} onClose={() => setPromiseModal(false)}>
          <div className="form-group">
            <label className="form-label">Promise Amount</label>
            <input className="form-input" type="number" value={promiseForm.promiseAmount} onChange={(e) => setPromiseForm((p) => ({ ...p, promiseAmount: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Promise Date</label>
            <input className="form-input" type="date" value={promiseForm.promiseDate} onChange={(e) => setPromiseForm((p) => ({ ...p, promiseDate: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-input" rows={2} value={promiseForm.notes} onChange={(e) => setPromiseForm((p) => ({ ...p, notes: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-outline" onClick={() => setPromiseModal(false)}>Cancel</button>
            <button className="btn-navy" onClick={submitPromise}>Record PTP</button>
          </div>
        </Modal>
      )}

      {paymentModal && (
        <Modal title="Record Payment" icon={<Banknote size={16} />} onClose={() => setPaymentModal(false)}>
          <div className="form-group">
            <label className="form-label">Amount</label>
            <input className="form-input" type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Mode</label>
            <select className="form-input" value={paymentForm.paymentMode} onChange={(e) => setPaymentForm((p) => ({ ...p, paymentMode: e.target.value }))}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Transaction Ref (optional)</label>
            <input className="form-input" value={paymentForm.transactionRef} onChange={(e) => setPaymentForm((p) => ({ ...p, transactionRef: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-input" rows={2} value={paymentForm.notes} onChange={(e) => setPaymentForm((p) => ({ ...p, notes: e.target.value }))} />
          </div>
          <div style={{ fontSize: '11px', color: 'var(--g3)', marginBottom: '8px' }}>
            <MapPin size={11} style={{ verticalAlign: 'middle' }} /> Your current location will be captured automatically as proof of collection. Next follow-up will be auto-scheduled 30 days from today if a balance remains.
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-outline" onClick={() => setPaymentModal(false)}>Cancel</button>
            <button className="btn-navy" onClick={submitPayment}>Record Payment</button>
          </div>
        </Modal>
      )}

      {assignModal && (
        <Modal title="Reassign Case" icon={<UserCog size={16} />} onClose={() => setAssignModal(false)}>
          <div className="form-group">
            <label className="form-label">Assigned To (Field)</label>
            <select className="form-input" value={assignForm.assignedToUserId} onChange={(e) => setAssignForm((p) => ({ ...p, assignedToUserId: e.target.value }))}>
              <option value="">— Unassigned —</option>
              {users
                .filter((u) => (u.role === 'agent' || u.role === 'manager') && (!loan?.branchId || u.branchId === loan.branchId))
                .sort((a, b) => (a.role === b.role ? a.name.localeCompare(b.name) : (a.role === 'agent' ? -1 : 1)))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.employeeId}){u.role === 'manager' ? ' — Manager (field)' : ''}
                  </option>
                ))}
            </select>
            <div style={{ fontSize: '11px', color: 'var(--g3)', marginTop: '4px' }}>
              Person who visits the field. Can be an agent or a manager doubling up as agent.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Supervising Manager (optional)</label>
            <select className="form-input" value={assignForm.supervisingManagerId} onChange={(e) => setAssignForm((p) => ({ ...p, supervisingManagerId: e.target.value }))}>
              <option value="">— None —</option>
              {users.filter((u) => u.role === 'manager' && (!loan?.branchId || u.branchId === loan.branchId)).map((u) => (
                <option key={u.id} value={u.id}>{u.name} ({u.employeeId})</option>
              ))}
            </select>
            <div style={{ fontSize: '11px', color: 'var(--g3)', marginTop: '4px' }}>
              Leave empty if the assignee is already a manager who supervises themselves.
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button className="btn-outline" onClick={() => setAssignModal(false)}>Cancel</button>
            <button className="btn-navy" onClick={submitAssign}>Save Assignment</button>
          </div>
        </Modal>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}

function Modal({ title, icon, onClose, children }) {
  return (
    <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="owner-modal" style={{ width: '460px' }}>
        <div className="modal-head">
          <div className="modal-icon">{icon}</div>
          <div>
            <div className="modal-title">{title}</div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function AuditRow({ row }) {
  const ENTITY_LABEL = { action: 'Action', promise: 'PTP', payment: 'Payment' };
  const isDelete = row.action === 'delete';
  const ts = (() => { try { return new Date(row.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }); } catch { return row.createdAt; } })();
  const renderVal = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  const before = row.before || {};
  const after = row.after || {};
  const fields = (row.changedFields || []).filter((f) => !['createdAt', '_id', 'id'].includes(f));
  return (
    <div style={{ background: '#fafbfc', border: '1px solid var(--g2)', borderRadius: '8px', padding: '8px 10px', fontSize: '11.5px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ background: isDelete ? '#fee2e2' : '#dbeafe', color: isDelete ? 'var(--red)' : 'var(--navy)', fontWeight: 700, padding: '1px 6px', borderRadius: '4px', fontSize: '10px', textTransform: 'uppercase' }}>
          {isDelete ? 'Deleted' : 'Edited'} {ENTITY_LABEL[row.entityKind] || row.entityKind}
        </span>
        <span style={{ color: 'var(--g3)' }}>by</span>
        <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{row.userName || row.userEmployeeId || row.userId}</span>
        <span style={{ color: 'var(--g3)' }}>({row.userRole})</span>
        <span style={{ marginLeft: 'auto', color: 'var(--g3)' }}>{ts}</span>
      </div>
      {row.reason && (
        <div style={{ marginTop: '4px', color: 'var(--g3)' }}>
          Reason: <span style={{ color: 'var(--navy)' }}>{row.reason}</span>
        </div>
      )}
      {isDelete ? (
        <div style={{ marginTop: '4px', color: 'var(--g3)' }}>
          Removed: {Object.entries(before).filter(([k]) => ['amount', 'promiseAmount', 'promiseDate', 'actionType', 'outcome', 'notes'].includes(k)).map(([k, v]) => `${k}=${renderVal(v)}`).join(' · ') || '—'}
        </div>
      ) : fields.length > 0 ? (
        <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {fields.map((f) => (
            <div key={f} style={{ color: 'var(--g3)' }}>
              <strong style={{ color: 'var(--navy)' }}>{f}:</strong> <span style={{ textDecoration: 'line-through' }}>{renderVal(before[f])}</span> → <span style={{ color: 'var(--navy)', fontWeight: 600 }}>{renderVal(after[f])}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TimelineRow({ ev }) {
  const { kind, ts, data: d } = ev;
  let icon, color, title, body;
  if (kind === 'action') {
    icon = <MessageSquare size={14} />;
    color = 'var(--navy)';
    title = `${d.actionType?.replace(/_/g, ' ')}${d.outcome ? ` · ${d.outcome}` : ''}`;
    body = d.notes;
  } else if (kind === 'promise') {
    const effStatus = (d.effectiveStatus || d.status || 'pending').toLowerCase();
    const isMissed = effStatus === 'broken';
    const isKept = effStatus === 'kept';
    const isPartial = effStatus === 'partially_kept';
    if (isKept) {
      icon = <CheckCircle2 size={14} />;
      color = '#16a34a';
    } else if (isPartial) {
      icon = <HandCoins size={14} />;
      color = '#b45309';
    } else if (isMissed) {
      icon = <AlertTriangle size={14} />;
      color = 'var(--red)';
    } else {
      icon = <HandCoins size={14} />;
      color = '#0891b2';
    }
    title = `Promise to pay ${fmtINR(d.promiseAmount)} by ${fmtDay(d.promiseDate)}`;
    let label;
    if (isKept) label = `KEPT · paid ${fmtINR(d.paidAmount || d.promiseAmount)} ✓`;
    else if (isPartial) label = `PARTIALLY KEPT · paid ${fmtINR(d.paidAmount || 0)} of ${fmtINR(d.promiseAmount)}`;
    else if (isMissed) label = 'MISSED';
    else label = (d.status || 'pending').toUpperCase();
    body = `${label}${d.notes ? ` · ${d.notes}` : ''}`;
  } else {
    icon = <CheckCircle2 size={14} />;
    color = '#16a34a';
    title = `Payment ${fmtINR(d.amount)} · ${d.paymentMode}`;
    body = `${d.transactionRef ? `Ref ${d.transactionRef}` : ''}${d.notes ? ` · ${d.notes}` : ''}`;
  }
  const hasGps = d.gps_lat && d.gps_lng;
  const byName = d.createdByUserName || d.createdByEmployeeId;
  const byRole = d.createdByUserRole;
  return (
    <div style={{ display: 'flex', gap: '10px', padding: '10px 4px', borderBottom: '1px solid var(--g2)' }}>
      <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--g1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', color: 'var(--navy)', fontWeight: 600, textTransform: 'capitalize' }}>{title}</div>
        {body && <div style={{ fontSize: '12px', color: 'var(--g3)', marginTop: '2px' }}>{body}</div>}
        <div style={{ fontSize: '11px', color: 'var(--g4)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span>{fmtDate(ts)}</span>
          {byName && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'var(--navy)', fontWeight: 600 }}>
              <UserIcon size={11} /> by {byName}{byRole ? ` (${byRole})` : ''}
            </span>
          )}
          {hasGps && (
            <a
              href={`https://www.google.com/maps?q=${d.gps_lat},${d.gps_lng}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--navy)', display: 'inline-flex', alignItems: 'center', gap: '2px' }}
              onClick={(e) => e.stopPropagation()}
              title={`Location: ${Number(d.gps_lat).toFixed(5)}, ${Number(d.gps_lng).toFixed(5)}`}
            >
              <MapPin size={11} /> View location
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function OverdueMetaPanel({ overdueImport, reconciliations, loan }) {
  const recs = reconciliations || [];
  const openRecs = recs.filter((r) => r.status === 'open');
  const hasInfo = overdueImport || recs.length > 0;
  if (!hasInfo) return null;
  return (
    <div className="panel" style={{ marginTop: '16px' }}>
      <div className="panel-header">
        <span className="panel-title"><FileText size={14} /> Overdue Report &amp; Reconciliation</span>
        {openRecs.length > 0 && (
          <span style={{ background: '#fee2e2', color: 'var(--red)', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700 }}>
            {openRecs.length} open
          </span>
        )}
      </div>
      <div style={{ padding: '4px 0', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', fontSize: '12px' }}>
        <div>
          <div className="text-xs text-gray">Last report applied (system-wide)</div>
          <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{fmtDay(overdueImport?.reportDate)}</div>
          <div style={{ fontSize: '11px', color: 'var(--g3)' }}>
            Uploaded {fmtDate(overdueImport?.appliedAt)}{overdueImport?.appliedByName ? ` · by ${overdueImport.appliedByName}` : ''}
          </div>
          {overdueImport?.fileName && (
            <div style={{ fontSize: '11px', color: 'var(--g3)' }}>File: {overdueImport.fileName}</div>
          )}
        </div>
        <div>
          <div className="text-xs text-gray">This loan last seen in report</div>
          <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{fmtDay(overdueImport?.loanLastReportDate || loan?.cbsReportDate)}</div>
          {overdueImport?.reportDate && overdueImport?.loanLastReportDate && overdueImport.reportDate !== overdueImport.loanLastReportDate && (
            <div style={{ fontSize: '11px', color: 'var(--red)', fontWeight: 600 }}>
              Not present in latest report — may have been paid/closed in CBS
            </div>
          )}
        </div>
        <div>
          <div className="text-xs text-gray">Previous report date</div>
          <div style={{ color: 'var(--navy)', fontWeight: 600 }}>{fmtDay(overdueImport?.previousReportDate)}</div>
        </div>
      </div>

      {recs.length > 0 && (
        <div style={{ marginTop: '12px', borderTop: '1px solid var(--g2)', paddingTop: '10px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--navy)', marginBottom: '6px' }}>Reconciliation Differences</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {recs.map((r) => <ReconRow key={r.id} r={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function ReconRow({ r }) {
  const isOpen = r.status === 'open';
  const delta = Number(r.unexplainedDelta || 0);
  return (
    <div style={{ background: isOpen ? '#fffbeb' : '#f8fafc', border: `1px solid ${isOpen ? '#fbbf24' : 'var(--g2)'}`, borderRadius: 8, padding: '8px 10px', fontSize: '11.5px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        <span style={{ background: isOpen ? '#fbbf24' : '#cbd5e1', color: isOpen ? '#7c2d12' : 'var(--navy)', fontWeight: 700, padding: '1px 6px', borderRadius: 4, fontSize: 10, textTransform: 'uppercase' }}>
          {r.status}
        </span>
        <span style={{ color: 'var(--g3)' }}>Report</span>
        <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{fmtDay(r.reportDate)}</span>
        {r.previousReportDate && <span style={{ color: 'var(--g3)' }}>vs {fmtDay(r.previousReportDate)}</span>}
      </div>
      <div style={{ marginTop: 4, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '6px', color: 'var(--navy)' }}>
        <div>
          <div style={{ color: 'var(--g3)', fontSize: 10 }}>Previous O/S</div>
          <div>{fmtINR(r.previousOutstanding)}</div>
        </div>
        <div>
          <div style={{ color: 'var(--g3)', fontSize: 10 }}>Current O/S</div>
          <div>{fmtINR(r.currentOutstanding)}</div>
        </div>
        <div>
          <div style={{ color: 'var(--g3)', fontSize: 10 }}>O/S Change</div>
          <div style={{ color: (r.actualDrop || 0) > 0.5 ? '#16a34a' : ((r.actualDrop || 0) < -0.5 ? 'var(--red)' : 'var(--navy)'), fontWeight: 600 }} title={(r.actualDrop || 0) >= 0 ? 'Outstanding decreased (payments / write-offs)' : 'Outstanding increased (interest / penalty accrual)'}>
            {(r.actualDrop || 0) > 0.5 ? '↓ ' : ((r.actualDrop || 0) < -0.5 ? '↑ ' : '')}{fmtINR(Math.abs(r.actualDrop || 0))}
          </div>
        </div>
        <div>
          <div style={{ color: 'var(--g3)', fontSize: 10 }}>Portal Payments</div>
          <div>{fmtINR(r.portalPaymentsSince)}</div>
        </div>
        <div>
          <div style={{ color: 'var(--g3)', fontSize: 10 }}>Unexplained Δ</div>
          <div style={{ color: Math.abs(delta) > 0.5 ? 'var(--red)' : 'var(--navy)', fontWeight: 700 }}>{fmtINR(delta)}</div>
        </div>
      </div>
      {!isOpen && r.resolutionReason && (
        <div style={{ marginTop: 4, color: 'var(--g3)' }}>
          Resolved by {r.resolvedByName || '—'}: <span style={{ color: 'var(--navy)' }}>{r.resolutionReason}</span>
        </div>
      )}
    </div>
  );
}
